import { useCallback, useEffect, useReducer, useRef } from "react";

import { useConnectorPullMutation } from "@/api/dataset/useDatasetAPI";
import {
  ConnectorPullProgress,
  watchConnectorPull
} from "@/api/dataset/watchConnectorPull";
import { ERROR_MESSAGE } from "@/constants/message";
import { DatasetExploreItem } from "@/types/dataset";
import {
  readRunningPulls,
  removeRunningPull,
  saveRunningPull
} from "@/utils/runningPulls";

interface UseConnectorPullSyncProps {
  /** `addedRows` is what the pull brought in, counted against the row's total. */
  onSuccess: (dataset: DatasetExploreItem, addedRows: number) => void;
  onError: (dataset: DatasetExploreItem, error: Error) => void;
  /**
   * Called for every pull still running when this unmounts. The server keeps
   * pulling, but the stream that would have reported it is gone — so anything
   * left waiting on a result, a loading toast above all, has to be cleared here.
   */
  onAbandoned?: (datasetId: string) => void;
  /**
   * Where pulls under way are kept, so a page refresh can follow them again.
   * One key per page; left out, nothing is kept.
   */
  storageKey?: string;
  /** Called for each pull picked up again from storage when this mounts. */
  onResume?: (dataset: DatasetExploreItem) => void;
}

/** What a stored pull needs to be followed again after a refresh. */
interface StoredPull {
  dataset: DatasetExploreItem;
  operationId: string;
  /** The connector's figure for the record, from the pull request. */
  pulledTotalRows: number;
  /** How far its stream was read, so a resume carries on after that. */
  progress?: ConnectorPullProgress;
}

/**
 * Pulls connector-sourced datasets, several at once. The request only schedules
 * a pull, so each one is followed on its operation stream until it settles.
 *
 * A pull lasts only as long as this hook does: leaving the page aborts the
 * streams, and those pulls report through `onAbandoned` rather than through
 * `onSuccess`/`onError`.
 */
export const useConnectorPullSync = ({
  onSuccess,
  onError,
  onAbandoned,
  storageKey,
  onResume
}: UseConnectorPullSyncProps) => {
  const { mutateAsync: requestPull } = useConnectorPullMutation();

  /** Every pull under way, by dataset, held by what closes its stream. */
  const runningPulls = useRef<Map<string, AbortController>>(new Map());

  // A ref for two reasons: state alone cannot guard a second press, since React
  // applies the updater on the next render and both presses would read the
  // dataset as idle; and a getter that keeps its identity across renders is what
  // stops the row's action menu from being rebuilt — and closed — under the
  // press. Rows read that getter as they render, though, so the ref on its own
  // would never repaint them. This is what does.
  const [, repaintRows] = useReducer((tick: number) => tick + 1, 0);

  /**
   * Follows a pull on its stream until it settles, and only then drops the
   * stored entry. An abort means the page went away; a refresh resumes it.
   */
  const followPull = useCallback(
    async (storedPull: StoredPull, stream: AbortController) => {
      const { dataset, operationId, pulledTotalRows, progress } = storedPull;

      try {
        await watchConnectorPull({
          operationId,
          signal: stream.signal,
          resumeFrom: progress,
          onProgress: (latestProgress) => {
            if (!storageKey) return;
            saveRunningPull<StoredPull>(storageKey, dataset.id, {
              ...storedPull,
              progress: latestProgress
            });
          }
        });
        if (storageKey) removeRunningPull(storageKey, dataset.id);

        // The pull's total_rows is the connector's figure for the record, which
        // is where the dataset lands. Read from the request rather than the
        // stream, so the count never depends on which event ends the stream.
        //
        // Clamped because the record can hold fewer rows than the dataset does
        // — rows deleted at the source. There is no wording for rows going
        // away, so that reads as "nothing new" like a plain no-op.
        onSuccess(dataset, Math.max(0, pulledTotalRows - dataset.total_rows));
      } catch (error) {
        // An abort is this hook going away, which `onAbandoned` has reported
        // already; there is no one left to tell about it twice.
        if (stream.signal.aborted) return;

        if (storageKey) removeRunningPull(storageKey, dataset.id);
        onError(dataset, error as Error);
      }
    },
    [onError, onSuccess, storageKey]
  );

  /** Marks a dataset as pulling, runs `pull`, then clears the mark. */
  const trackPull = useCallback(
    async (
      datasetId: string,
      pull: (stream: AbortController) => Promise<void>
    ) => {
      const stream = new AbortController();
      runningPulls.current.set(datasetId, stream);
      repaintRows();

      try {
        await pull(stream);
      } finally {
        // A resume may have taken the dataset over since this pull was aborted.
        if (runningPulls.current.get(datasetId) === stream) {
          runningPulls.current.delete(datasetId);
        }
        repaintRows();
      }
    },
    []
  );

  const startSync = useCallback(
    async (dataset: DatasetExploreItem) => {
      const {
        id: datasetId,
        connector,
        connector_info: connectorInfo
      } = dataset;
      // Nothing to ask the connector for without the record it came from.
      if (!connector || !connectorInfo) return;
      if (runningPulls.current.has(datasetId)) return;

      await trackPull(datasetId, async (stream) => {
        let storedPull: StoredPull;
        try {
          const { items } = await requestPull({
            connector,
            connector_record_id: connectorInfo.record_id,
            connector_parent_id: connectorInfo.parent_id
          });
          const [scheduledPull] = items;

          if (!scheduledPull) {
            throw new Error(ERROR_MESSAGE.SOMETHING_WENT_WRONG);
          }

          storedPull = {
            dataset,
            operationId: scheduledPull.operation_id,
            pulledTotalRows: scheduledPull.total_rows
          };
        } catch (error) {
          if (!stream.signal.aborted) onError(dataset, error as Error);
          return;
        }

        if (storageKey) saveRunningPull(storageKey, datasetId, storedPull);
        await followPull(storedPull, stream);
      });
    },
    [followPull, onError, requestPull, storageKey, trackPull]
  );

  // Held in refs so the resume below runs once per page, on mount, and still
  // calls the latest callbacks.
  const latestFollowPull = useRef(followPull);
  latestFollowPull.current = followPull;
  const latestOnResume = useRef(onResume);
  latestOnResume.current = onResume;

  useEffect(() => {
    if (!storageKey) return;

    Object.values(readRunningPulls<StoredPull>(storageKey)).forEach(
      (storedPull) => {
        const datasetId = storedPull.dataset.id;
        if (runningPulls.current.has(datasetId)) return;

        latestOnResume.current?.(storedPull.dataset);
        trackPull(datasetId, (stream) =>
          latestFollowPull.current(storedPull, stream)
        );
      }
    );
  }, [storageKey, trackPull]);

  const isSyncing = useCallback(
    (datasetId: string) => runningPulls.current.has(datasetId),
    []
  );

  // Held in a ref so the cleanup below can run on unmount alone and still call
  // the callback the consumer passed last, not the one from the first render.
  const latestOnAbandoned = useRef(onAbandoned);
  latestOnAbandoned.current = onAbandoned;

  useEffect(
    () => () => {
      // Every pull still here is one nothing will report any more: its stream
      // closes with this hook, and a request yet to come back lands on a hook
      // that is gone.
      runningPulls.current.forEach((stream, datasetId) => {
        stream.abort();
        latestOnAbandoned.current?.(datasetId);
      });
      // Cleared now rather than as each aborted pull winds down, so a remount
      // (StrictMode, or returning to the page) resumes them from storage.
      runningPulls.current.clear();
    },
    []
  );

  return { startSync, isSyncing };
};
